/* __next_internal_client_entry_do_not_use__ Price auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { SERRANO } from "@faire/retailer-visitor-shared/consts/colors";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useSettingProminentPriceFirstProductTile } from "@faire/retailer-visitor-shared/settings/useSettingProminentPriceFirstProductTile";
import { useGetSettingAndAssignUpdateSaleColorCopy } from "@faire/retailer-visitor-shared/settings/useSettingUpdateSaleColorCopy";
import { RedesignedOutOfStockText } from "@faire/retailer-visitor-shared/views/product/ProductDetails/__internal__/Header/RedesignedOutOfStockText";
import { PRICE_TEXT_SURFACE } from "@faire/retailer-visitor-shared/views/product/ProductDetails/Price/PriceProps";
import { ProminentPrice } from "@faire/retailer-visitor-shared/views/product/ProductDetails/Price/ProminentPrice";
import { DEPRECATED_backwardsCompatibleValues, useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { LockSquare, Lock } from "@faire/web--source/slate/icons/foundation/Lock";
import { Flex, Row } from "@faire/web--source/slate/Layout";
import { Tooltip } from "@faire/web--source/slate/Tooltip";
import { Span as TypographySpan, Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import React from "react";
import styled from "styled-components";
var useShouldShowProminentPrice = function(isPdp) {
    var isLoggedInRetailer = useIsLoggedInRetailer();
    return useSettingProminentPriceFirstProductTile() && isLoggedInRetailer && !isPdp;
};
var RecommendedRetailPricePromoText = function(param) {
    var originalPrice = param.originalPrice, wholesalePrice = param.wholesalePrice, retailPrice = param.retailPrice, small = param.small;
    var shouldUpdateAndAssignSaleColor = useGetSettingAndAssignUpdateSaleColorCopy();
    var renderPromoFormatting = function(chunk) {
        return /*#__PURE__*/ _jsx(TypographySpan, {
            color: shouldUpdateAndAssignSaleColor ? SERRANO : Decorative.shade.orange,
            variant: small ? "paragraphSansMedium" : "subheadingSansMedium",
            children: chunk
        });
    };
    return /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "BxFhU1",
        defaultMessage: [
            {
                type: 8,
                value: "PromoFormatting",
                children: [
                    {
                        type: 1,
                        value: "wholesaleAmountMoney"
                    }
                ]
            },
            {
                type: 0,
                value: " "
            },
            {
                type: 8,
                value: "StrikeThroughFormatting",
                children: [
                    {
                        type: 1,
                        value: "originalAmountMoney"
                    }
                ]
            },
            {
                type: 0,
                value: " "
            },
            {
                type: 8,
                value: "TextFormatting",
                children: [
                    {
                        type: 0,
                        value: "RRP "
                    },
                    {
                        type: 1,
                        value: "retailAmountMoney"
                    }
                ]
            }
        ],
        values: {
            PromoFormatting: renderPromoFormatting,
            StrikeThroughFormatting: renderStrikeThroughFormatting,
            TextFormatting: renderRegularChunkGrey,
            originalAmountMoney: originalPrice,
            wholesaleAmountMoney: wholesalePrice,
            retailAmountMoney: retailPrice
        }
    });
};
var PromoText = function(param) {
    var originalPrice = param.originalPrice, wholesalePrice = param.wholesalePrice, retailPrice = param.retailPrice, isTilePricing = param.isTilePricing, small = param.small;
    var shouldUpdateAndAssignSaleColor = useGetSettingAndAssignUpdateSaleColorCopy();
    var renderPromoFormatting = function(chunk) {
        return /*#__PURE__*/ _jsx(TypographySpan, {
            color: shouldUpdateAndAssignSaleColor ? SERRANO : Decorative.shade.orange,
            variant: small ? "paragraphSansMedium" : "subheadingSansMedium",
            children: chunk
        });
    };
    return /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "46W+4T",
        defaultMessage: [
            {
                type: 8,
                value: "PromoFormatting",
                children: [
                    {
                        type: 1,
                        value: "wholesaleAmountMoney"
                    }
                ]
            },
            {
                type: 0,
                value: " "
            },
            {
                type: 8,
                value: "StrikeThroughFormatting",
                children: [
                    {
                        type: 1,
                        value: "originalAmountMoney"
                    }
                ]
            },
            {
                type: 0,
                value: " "
            },
            {
                type: 8,
                value: "TextFormatting",
                children: [
                    {
                        type: 0,
                        value: "MSRP "
                    },
                    {
                        type: 1,
                        value: "retailAmountMoney"
                    }
                ]
            }
        ],
        values: {
            PromoFormatting: renderPromoFormatting,
            TextFormatting: isTilePricing ? renderRegularChunkGrey : renderRegularChunk,
            StrikeThroughFormatting: renderStrikeThroughFormatting,
            originalAmountMoney: originalPrice,
            wholesaleAmountMoney: wholesalePrice !== null && wholesalePrice !== void 0 ? wholesalePrice : "",
            retailAmountMoney: retailPrice
        }
    });
};
var RecommendedRetailPriceText = function(param) {
    var wholesalePrice = param.wholesalePrice, retailPrice = param.retailPrice, small = param.small;
    return /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "meEZF0",
        defaultMessage: [
            {
                type: 8,
                value: "BoldFormatting",
                children: [
                    {
                        type: 1,
                        value: "wholesaleAmountMoney"
                    }
                ]
            },
            {
                type: 0,
                value: " "
            },
            {
                type: 8,
                value: "TextFormatting",
                children: [
                    {
                        type: 0,
                        value: "RRP "
                    },
                    {
                        type: 1,
                        value: "retailAmountMoney"
                    }
                ]
            }
        ],
        values: {
            TextFormatting: renderRegularChunkGrey,
            BoldFormatting: small ? renderParagraphBoldFormatting : renderSubHeadingBoldFormatting,
            wholesaleAmountMoney: wholesalePrice !== null && wholesalePrice !== void 0 ? wholesalePrice : "",
            retailAmountMoney: retailPrice
        }
    });
};
var PriceText = function(param) {
    var wholesalePrice = param.wholesalePrice, retailPrice = param.retailPrice, isTilePricing = param.isTilePricing, priceTextSurface = param.priceTextSurface, isLoggedInOrBrandPreview = param.isLoggedInOrBrandPreview;
    var getWholesaleTextFormatting = function() {
        if (priceTextSurface === PRICE_TEXT_SURFACE.PDP) {
            return function(chunk) {
                return renderSubHeadingBoldFormatting(chunk, "4px");
            };
        }
        return renderRegularChunk;
    };
    if (priceTextSurface === PRICE_TEXT_SURFACE.BRAND) {
        if (!isLoggedInOrBrandPreview) {
            return /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "1ugUmG",
                defaultMessage: [
                    {
                        type: 8,
                        value: "TextFormatting",
                        children: [
                            {
                                type: 0,
                                value: "MSRP "
                            },
                            {
                                type: 1,
                                value: "retailAmountMoney"
                            }
                        ]
                    }
                ],
                values: {
                    TextFormatting: isLoggedInOrBrandPreview ? renderRegularChunkGrey : renderRegularChunk,
                    retailAmountMoney: retailPrice
                }
            });
        }
    }
    return /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "MnVXNH",
        defaultMessage: [
            {
                type: 8,
                value: "WholesaleTextFormatting",
                children: [
                    {
                        type: 1,
                        value: "wholesaleAmountMoney"
                    }
                ]
            },
            {
                type: 0,
                value: " "
            },
            {
                type: 8,
                value: "TextFormatting",
                children: [
                    {
                        type: 0,
                        value: "MSRP "
                    },
                    {
                        type: 1,
                        value: "retailAmountMoney"
                    }
                ]
            }
        ],
        values: {
            TextFormatting: isTilePricing ? renderRegularChunkGrey : renderRegularChunk,
            WholesaleTextFormatting: getWholesaleTextFormatting(),
            wholesaleAmountMoney: wholesalePrice !== null && wholesalePrice !== void 0 ? wholesalePrice : "",
            retailAmountMoney: retailPrice
        }
    });
};
var PriceBase = function(props) {
    var strictLocalize = useStrictLocalization().strictLocalize;
    if (props.showProductPromotions) {
        return /*#__PURE__*/ _jsx(Row, {
            gap: "1x",
            align: "center",
            "data-test-id": "promotion-text",
            "aria-label": strictLocalize({
                id: "YFDyy0",
                defaultMessage: [
                    {
                        type: 0,
                        value: "original price is "
                    },
                    {
                        type: 1,
                        value: "originalAmountMoney"
                    },
                    {
                        type: 0,
                        value: ", discounted price is "
                    },
                    {
                        type: 1,
                        value: "wholesaleAmountMoney"
                    }
                ]
            }, DEPRECATED_backwardsCompatibleValues({
                originalAmountMoney: props.originalPrice,
                wholesaleAmountMoney: props.wholesalePrice
            })),
            children: /*#__PURE__*/ _jsx(Typography, {
                children: props.isUKRetailer ? /*#__PURE__*/ _jsx(RecommendedRetailPricePromoText, _object_spread({}, props)) : /*#__PURE__*/ _jsx(PromoText, _object_spread({}, props))
            })
        });
    } else {
        return /*#__PURE__*/ _jsx(Row, {
            gap: "1x",
            children: /*#__PURE__*/ _jsx(PriceTextWithMaybeLoggedOutLock, {
                priceTextSurface: props.priceTextSurface,
                isLoggedInOrBrandPreview: props.isLoggedInOrBrandPreview,
                children: /*#__PURE__*/ _jsx(Typography, {
                    "data-test-id": "product-tile-msrp",
                    children: props.isUKRetailer ? /*#__PURE__*/ _jsx(RecommendedRetailPriceText, _object_spread({}, props)) : /*#__PURE__*/ _jsx(PriceText, _object_spread({}, props))
                })
            })
        });
    }
};
var PriceTextWithMaybeLoggedOutLock = function(param) {
    var children = param.children, priceTextSurface = param.priceTextSurface, isLoggedInOrBrandPreview = param.isLoggedInOrBrandPreview;
    if (priceTextSurface === PRICE_TEXT_SURFACE.BRAND) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                children,
                !isLoggedInOrBrandPreview ? /*#__PURE__*/ _jsx(LockWithTooltip, {}) : null
            ]
        });
    } else {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                !isLoggedInOrBrandPreview ? /*#__PURE__*/ _jsxs(Row, {
                    align: "center",
                    gap: 4,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "OXigr+",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "WSP"
                                    }
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(LockIconMaybeTooltip, {})
                    ]
                }) : null,
                children
            ]
        });
    }
};
export var Price = function(props) {
    var shouldShowProminentPrice = useShouldShowProminentPrice(props.priceTextSurface === PRICE_TEXT_SURFACE.PDP);
    var isLoggedInRetailer = useIsLoggedInRetailer();
    return /*#__PURE__*/ _jsxs(Row, {
        align: "baseline",
        gap: "1x",
        "data-test-id": "product-price",
        children: [
            shouldShowProminentPrice && isLoggedInRetailer ? /*#__PURE__*/ _jsx(ProminentPrice, _object_spread({}, props)) : /*#__PURE__*/ _jsx(PriceBase, _object_spread({}, props)),
            props.priceTextSurface !== PRICE_TEXT_SURFACE.BRAND ? /*#__PURE__*/ _jsx(RedesignedOutOfStockText, {
                isOutOfStock: props.isOutOfStock,
                showOutOfStock: !props.showOutOfStockUnderProductName,
                dataTestId: "out-of-stock-text-in-Price"
            }) : null
        ]
    });
};
var LockIconMaybeTooltip = function() {
    var isTabletAndBelow = useViewport().isTabletAndBelow;
    if (isTabletAndBelow) {
        return /*#__PURE__*/ _jsx(LockSquare, {
            width: "12px",
            height: "12px"
        });
    }
    var signUpButton = /*#__PURE__*/ _jsx(Button, {
        tertiaryColor: Core.text.inverse,
        variant: "tertiary",
        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "EuCY1R",
            defaultMessage: [
                {
                    type: 0,
                    value: "Sign up for free"
                }
            ]
        })
    });
    return /*#__PURE__*/ _jsxs(Flex, {
        style: {
            position: "relative"
        },
        "data-test-id": "logged-out-product-tile-wholesale-price",
        children: [
            /*#__PURE__*/ _jsx(LockSquare, {
                style: {
                    position: "absolute",
                    width: "12px",
                    height: "12px"
                },
                width: "12px",
                height: "12px"
            }),
            /*#__PURE__*/ _jsx(Tooltip, {
                position: "top",
                pointerOffset: -60,
                targetNode: /*#__PURE__*/ _jsx("div", {
                    style: {
                        height: "12px",
                        width: "12px",
                        color: "transparent"
                    }
                }),
                action: signUpButton,
                children: /*#__PURE__*/ _jsx(Typography, {
                    color: Core.text.inverse,
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "FNsBy5",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Join Faire to unlock wholesale pricing."
                            }
                        ]
                    })
                })
            })
        ]
    });
};
var LockWithTooltip = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    return /*#__PURE__*/ _jsx(ResponsiveContentV2, {
        mobile: /*#__PURE__*/ _jsx(Lock, {
            lockType: "foundation"
        }),
        tabletAndAbove: /*#__PURE__*/ _jsx(Tooltip, {
            position: "top",
            contents: [
                {
                    body: strictLocalize({
                        id: "U97XRu",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Sign up for Faire to unlock wholesale pricing"
                            }
                        ]
                    })
                }
            ],
            icon: /*#__PURE__*/ _jsx(Lock, {
                lockType: "foundation"
            }),
            width: 220
        })
    });
};
var renderRegularChunkGrey = function(chunk) {
    return /*#__PURE__*/ _jsx(TypographySpan, {
        color: Core.text.subdued,
        variant: "paragraphSansRegular",
        children: chunk
    });
};
var renderRegularChunk = function(chunk) {
    return /*#__PURE__*/ _jsx(TypographySpan, {
        variant: "paragraphSansRegular",
        children: chunk
    });
};
var renderStrikeThroughFormatting = function(chunk) {
    return /*#__PURE__*/ _jsx(StyledStrikethrough, {
        children: chunk
    });
};
var renderParagraphBoldFormatting = function(chunk) {
    return /*#__PURE__*/ _jsx(TypographySpan, {
        variant: "subheadingSansMedium",
        children: chunk
    });
};
var renderSubHeadingBoldFormatting = function(chunk, marginRight) {
    return /*#__PURE__*/ _jsx(TypographySpan, {
        variant: "subheadingSansMedium",
        style: {
            marginRight: marginRight
        },
        children: chunk
    });
};
var StyledStrikethrough = styled(TypographySpan).attrs({
    variant: "paragraphSansRegular"
}).withConfig({
    componentId: "sc-86603625-0"
})([
    "text-decoration:line-through;"
]);
